/**
 * Load the Google Maps JS API once per page.
 *
 * Two surfaces need it — the gym selector's map view and the platform market
 * map — and each had its own copy of the loader. They disagreed: the platform
 * one looked for an existing `#sendly-gmaps` tag before injecting, the gym
 * selector did not. So opening the gym-selector map twice (it is a toggle),
 * or opening it after the market map, appended a SECOND `maps/api/js` script.
 * Google's loader logs "You have included the Google Maps JavaScript API
 * multiple times", the second load can clobber the first's `window.google`
 * mid-session, and every extra tag is a billed map load.
 *
 * The id guard alone was not enough either: attaching a `load` listener to a
 * script that has ALREADY fired never resolves, so a caller arriving late
 * would wait forever. Hence a module-level promise — the single fact of
 * "loading has started" — rather than a DOM lookup.
 *
 * A rejection clears the cache so a retry (both callers offer one) can try
 * again instead of replaying the failure forever.
 *
 * The script tag is deliberately hand-built rather than using
 * `@googlemaps/js-api-loader`, which sends the key under the wrong parameter
 * name for this project's key restrictions.
 */

const SCRIPT_ID = "sendly-gmaps";

let loadPromise = null;

/**
 * @param {string} apiKey  the platform-config Maps key
 * @param {string} [libraries] comma-separated libraries, default "marker"
 * @returns {Promise<object>} resolves with `window.google.maps`
 */
export function loadGoogleMaps(apiKey, libraries = "marker") {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (loadPromise) return loadPromise;

  const key = (apiKey || "").trim();
  if (!key) return Promise.reject(new Error("Google Maps API key missing"));

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const script = existing || document.createElement("script");
    const done = () => {
      if (window.google?.maps) resolve(window.google.maps);
      else reject(new Error("Google Maps loaded without a maps namespace"));
    };
    script.addEventListener("load", done);
    script.addEventListener("error", () => reject(new Error("Failed to load Google Maps script")));
    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=${encodeURIComponent(libraries)}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  loadPromise.catch(() => { loadPromise = null; });
  return loadPromise;
}

/** Test seam — forget that a load was ever started. Never called by app code. */
export function __resetGoogleMapsLoaderForTests() {
  loadPromise = null;
}
